import { DeleteIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/outline';
import axios from 'axios';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  DocumentStatus,
  FundraisingDocumentType,
  UserRole,
  useDeleteSubscriptionCustomDocumentMutation,
  useGetDocumentPdfUrlLazyQuery,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { getDocumentIds } from '@blockpulse3/helpers';
import {
  DeleteConfirmModal,
  DocumentLink,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';
import { useUser } from '@blockpulse3/web-client/auth';

type DocumentTypeMessage = keyof Messages['DocumentValues'];

type Props = {
  subscriptionId: string;
};

/**
 * SubscriptionDetailsDocuments.
 * Documents section of the subscription side panel.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsDocuments({ subscriptionId }: Props): JSX.Element {
  const i18nDocumentTitle = useTranslations('DocumentValues');
  const i18nDocumentStatusValues = useTranslations('DocumentStatusValues');
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');
  const i18nSubscriptionDocumentsList = useTranslations('SubscriptionDocumentsList');

  const { user } = useUser();

  const inputRef = useRef<HTMLInputElement>(null);
  const deleteRef = useRef(null);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const deleteModal = useDisclosure();

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  /* ** Cached subscription query ** */
  const { data, loading, refetch } = useGetSubscriptionQuery({ variables: { subscriptionId } });

  /* ** Query to get pdf url of a document ** */
  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();
  const [deleteCustomDocument, { loading: isCustomDocumentDeletionLoading }] =
    useDeleteSubscriptionCustomDocumentMutation();

  const subscription = data?.subscription;
  const subscriptionDocuments = subscription?.subscriptionDocuments || [];
  const subscriptionDocument = subscriptionDocuments.find(
    (doc) => doc.type === FundraisingDocumentType.SUBSCRIPTION,
  );

  const documents = getDocumentIds(subscriptionDocuments);
  const holdingMethodCertificate = subscriptionDocuments.find(
    (doc) =>
      doc.status === DocumentStatus.SIGNED &&
      [
        FundraisingDocumentType.IR_PME_CERTIFICATE,
        FundraisingDocumentType.PEA_CERTIFICATE,
        FundraisingDocumentType.OWNERSHIP_CERTIFICATE,
        FundraisingDocumentType.PEA_PME_CERTIFICATE,
      ].includes(doc.type as FundraisingDocumentType),
  );

  const customFiles = subscriptionDocuments.filter(
    (doc) => doc.type === FundraisingDocumentType.CUSTOM_FILE,
  );

  /* ** Participation document status badge ** */
  const subscriptionDocumentStatus = useBadge(
    subscriptionDocument?.status,
    [
      { value: DocumentStatus.SIGNED, color: 'green', label: i18nDocumentStatusValues('SIGNED') },
      { value: DocumentStatus.ONGOING, color: 'gray', label: i18nDocumentStatusValues('ONGOING') },
      {
        value: DocumentStatus.CANCELLED,
        color: 'red',
        label: i18nDocumentStatusValues('CANCELLED'),
      },
      {
        value: DocumentStatus.SIGNATURES_PENDING,
        color: 'yellow',
        label: i18nDocumentStatusValues('SIGNATURES_PENDING'),
      },
    ],
    { color: 'gray', label: i18nDocumentStatusValues('ONGOING') },
  );

  const handleDocumentClick = (documentId: string): void => {
    getDocumentPdfUrl({
      variables: {
        documentId,
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
        window.open(pdfUrl, '_blank');
      },
    });
  };

  const handleAddDocumentClick = (): void => {
    inputRef?.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files;

    if (!files?.[0]) return;

    const formData = new FormData();
    formData.append('subscriptionDocument', files[0]);
    formData.append('subscriptionId', subscriptionId);
    formData.append('documentType', FundraisingDocumentType.CUSTOM_FILE);

    setIsUploadLoading(true);
    await axios
      .post(
        process.env['NX_API_CONTROLLER_ENDPOINT'] + '/subscriptions/uploadSubscriptionDocument',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(() => {
        successToast({ title: i18nSubscriberDetailsSidePanel('Toast.DocumentUploadSuccess') });
      })
      .catch(() => {
        errorToast({ title: i18nSubscriberDetailsSidePanel('Toast.DocumentUploadError') });
      });

    await refetch();
    setIsUploadLoading(false);
  };

  const handleFileDelete = (fileId: string): void => {
    setSelectedFileId(fileId);
    deleteModal.onOpen();
  };

  const handleDelete = (): void => {
    if (!selectedFileId) return;

    deleteCustomDocument({
      variables: {
        documentId: selectedFileId,
      },
      onCompleted: () => {
        refetch();
      },
    });

    deleteModal.onClose();
  };

  const isUserAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );

  return (
    <Stack px="4" spacing="4">
      <HStack justifyContent="space-between">
        <Text color="gray.600" fontWeight="600">
          {i18nSubscriberDetailsSidePanel('Documents', { len: subscriptionDocuments.length })}
        </Text>
        <form id="add-document-form">
          <Input
            accept=".pdf"
            display="none"
            ref={inputRef}
            type="file"
            onChange={handleFileInputChange}
          />
          <Button
            alignSelf="flex-start"
            disabled={isUploadLoading}
            isLoading={isUploadLoading}
            leftIcon={<Icon as={PlusIcon} />}
            loadingText="Chargement"
            size="sm"
            variant="secondary"
            onClick={handleAddDocumentClick}
          >
            {i18nSubscriberDetailsSidePanel('UploadCustomDocument')}
          </Button>
        </form>
      </HStack>
      {loading ? (
        <Skeleton h="60px" />
      ) : (
        <Stack spacing="2">
          <DocumentLink
            fileName={i18nDocumentTitle('DocumentSUBSCRIPTION')}
            isActive={!!documents[FundraisingDocumentType.SUBSCRIPTION]}
            onClick={(): void =>
              handleDocumentClick(documents[FundraisingDocumentType.SUBSCRIPTION])
            }
          >
            <Badge colorScheme={subscriptionDocumentStatus.badge.color} fontSize="xs">
              {subscriptionDocumentStatus.badge.label}
            </Badge>
          </DocumentLink>
          {documents[FundraisingDocumentType.IBAN] && (
            <DocumentLink
              fileName={i18nDocumentTitle('DocumentIBAN')}
              onClick={(): void => handleDocumentClick(documents[FundraisingDocumentType.IBAN])}
            />
          )}
          {documents[FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE] && (
            <DocumentLink
              fileName={i18nDocumentTitle('DocumentFUNDS_SOURCE_CERTIFICATE')}
              onClick={(): void =>
                handleDocumentClick(documents[FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE])
              }
            />
          )}
          {holdingMethodCertificate && (
            <DocumentLink
              fileName={i18nDocumentTitle(
                `Document${holdingMethodCertificate.type}` as DocumentTypeMessage,
              )}
              onClick={(): void => handleDocumentClick(holdingMethodCertificate.id)}
            />
          )}
          {customFiles.map((file, index) => {
            const fileName =
              file.title === 'CUSTOM_FILE'
                ? `${i18nSubscriptionDocumentsList('CUSTOM_FILE')} ${++index}`
                : file.title;
            return (
              <HStack key={file.id} justify="space-between">
                <DocumentLink
                  fileName={fileName}
                  onClick={(): void => handleDocumentClick(file.id)}
                />
                {file.parentDocumentType === 'SubscriptionDocument' && isUserAuthorized && (
                  <IconButton
                    aria-label="delete-document"
                    icon={<DeleteIcon color="red.500" />}
                    isDisabled={isCustomDocumentDeletionLoading}
                    size="xs"
                    variant="ghost"
                    onClick={(): void => handleFileDelete(file.id)}
                  />
                )}
              </HStack>
            );
          })}
        </Stack>
      )}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        leastDestructiveRef={deleteRef}
        subtitle={i18nSubscriberDetailsSidePanel('DeleteDocumentModal.SubTitle')}
        title={i18nSubscriberDetailsSidePanel('DeleteDocumentModal.Title')}
        onClose={deleteModal.onClose}
        onDelete={handleDelete}
      />
    </Stack>
  );
}

export type SubscriptionDetailsDocumentsProps = Props;
